import { useCallback, useEffect, useState } from "react";
import { BrainCircuit, Check, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";

/** Which brain answers. Claude and Gemini both run on the one Emergent key, so switching a job is
 *  a dropdown — it takes effect on the next answer, with no redeploy. */
export const AiModelPanel = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [saved, setSaved] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/ai-models");
      setData(res.data);
    } catch (e) { setError(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pick = async (job, choice) => {
    setBusy(job); setError(""); setSaved("");
    try {
      const { data: res } = await api.put("/admin/ai-models", { job, choice });
      setData((d) => ({
        ...d,
        jobs: d.jobs.map((j) => (j.id === job ? { ...j, choice: res.choice, label_now: res.label } : j)),
      }));
      setSaved(job);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  if (!data) {
    return (
      <div className="grid place-items-center py-16">
        {error ? <p data-testid="ai-models-error" className="text-sm text-red-400">{error}</p>
          : <Loader2 className="h-5 w-5 animate-spin text-gold" />}
      </div>
    );
  }

  return (
    <div data-testid="ai-models-panel" className="space-y-5">
      <div className="rounded-xl border border-ink-600 bg-ink-800 p-6">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
          <BrainCircuit className="h-3.5 w-3.5" /> AI models
        </p>
        <p className="mt-3 max-w-2xl text-sm text-slate-400">
          Pick the model behind each job. Every one of these runs on your Emergent universal key —
          there is no Anthropic or Google account to connect — and a change takes effect on the next
          answer. Opus costs materially more per answer than Sonnet; Haiku costs the least.
        </p>
        {data.updated_at && (
          <p data-testid="ai-models-updated" className="mt-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            Last changed {new Date(data.updated_at).toLocaleString()}
            {data.updated_by ? ` · ${data.updated_by}` : ""}
          </p>
        )}
        {error && <p data-testid="ai-models-error" className="mt-3 text-sm text-red-400">{error}</p>}
      </div>

      {data.jobs.map((job) => (
        <div key={job.id} data-testid={`ai-job-${job.id}`}
          className="rounded-xl border border-ink-600 bg-ink-800 p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="max-w-xl">
              <p className="font-display text-lg text-slate-100">{job.label}</p>
              <p className="mt-1.5 text-sm text-slate-400">{job.about}</p>
            </div>
            <span data-testid={`ai-job-current-${job.id}`}
              className="rounded-full border border-gold/30 bg-gold/10 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-gold">
              {job.label_now}
              {saved === job.id && <Check className="ml-1.5 inline h-3 w-3" />}
            </span>
          </div>

          <div className="mt-5 grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
            {data.choices.map((choice) => {
              const on = job.choice === choice.id;
              return (
                <button key={choice.id} type="button"
                  data-testid={`ai-pick-${job.id}-${choice.id}`}
                  disabled={busy === job.id}
                  onClick={() => pick(job.id, choice.id)}
                  className={`rounded-xl border p-4 text-left transition-colors duration-200 ${
                    on ? "border-gold/60 bg-gold/10"
                       : "border-ink-600 bg-ink-900/60 hover:border-gold/40"}`}>
                  <span className="flex items-center justify-between gap-2">
                    <span className={`text-sm font-semibold ${on ? "text-gold" : "text-slate-100"}`}>
                      {choice.label}
                    </span>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500">
                      {choice.family}
                    </span>
                  </span>
                  <span className="mt-1.5 block text-xs leading-relaxed text-slate-400">
                    {choice.note}
                  </span>
                  {data.defaults[job.id] === choice.id && (
                    <span className="mt-2 block font-mono text-[9px] uppercase tracking-widest text-slate-600">
                      Recommended
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
